import { EXPERIMENTS_HEADER } from '@noths/polaris-client-experiments';
import { isBrowser } from '@noths/polaris-client-feature-capabilities';
import {
  selectRouterPathname,
  selectRouterQueryParameters,
} from '@noths/polaris-client-next-redux';
import { fetchWithTimeout, handleFetchResponse } from '@noths/polaris-client-utils';
import { CurrencyCode, Navigation } from '@noths/polaris-dev-ts-types';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { replace } from 'connected-next-router';
import fetch from 'isomorphic-fetch';
import getConfig from 'next/config';
import { AbortController } from 'node-abort-controller';
import qs, { ParsedUrlQueryInput } from 'querystring';

import { PRODUCT_CONTAINER_WRAPPER } from 'src/constants/elementIds';
import {
  Category,
  Filter,
  NextConfig,
  PageType,
  Partner,
  Product,
  ProductsAPIPath,
  SortOption,
} from 'src/types';
import { ReduxApplicationState } from '../combinedReducer';
import { selectCurrentPage } from '../selectors';
import { addLoadingProducts } from './actions';
import {
  selectBrowseApiPath,
  selectPartnerShortcode,
  selectRequestOptionsAsQueryObject,
} from './selectors';
import { ProductsState } from './types';
const { publicRuntimeConfig } = getConfig() as NextConfig;
import { selectPageType } from 'src/redux/navigation';
import { pageHistory } from 'src/services/pageHistory';

const SERVER_SIDE_TIMEOUT = 4000;

export interface ProductsAPIResponse {
  currencyCode: CurrencyCode;
  category: Category;
  products: Product[];
  filters: Filter[];
  currentPage: number;
  totalPages: number;
  totalProducts: number;
  productsReturnedCount: number;
  queryType: string;
  sortingOptions: SortOption[];
  navigation: Navigation[];
  partner?: Partner;
  errorCode?: string;
  errorMessage?: string;
}

interface RequestProductsActionPayload extends Omit<ProductsAPIResponse, 'products'> {
  searchTerm?: string;
  items: Record<number, Product[]>;
}

export interface RequestProductsOptions {
  query: ParsedUrlQueryInput;
  cookie?: string;
  replaceProducts?: boolean;
  isClientRequest?: boolean;
}

interface ProductsAPIHandledRejection {
  redirectUrl?: string;
  isServerTimeout?: boolean;
  isClientAbort?: boolean;
}

export enum UpdateFiltersActionType {
  SORT = 'SORT',
  FILTER = 'FILTER',
}

export type UpdateHistoryPayload = {
  page: number;
  productCode: Product['code'];
  productOffset: number;
};

export type FetchProductsWithServerSideTimeoutOptions = {
  isClientRequest?: boolean;
  browseApiEndpoint: string;
  browseApiPath?: ProductsAPIPath;
  querystring: string;
  cookie?: string;
  cognitoId?: string;
  elevaateSessionId?: string;
  experiments?: string;
};

const canRequestSkeletonLoaders = (
  { currentPage }: ProductsState,
  { page }: qs.ParsedUrlQueryInput = {},
) => {
  const isInitialPageLoad = currentPage === 0;
  const isLoadingNewPage = page != null;

  return isInitialPageLoad || !isLoadingNewPage;
};

const getQueryForHistoryState = (page: number) => {
  // No need to do anything else if page not present in the browser, because it's always implicity page=1.
  return `${location.search.replace(/page=\d+/, `page=${page}`)}`;
};

// only provide abort controller when on client to avoid aborting previous requests on server
const getAbortController = () =>
  isBrowser()
    ? new AbortController()
    : (({ signal: null, abort: () => {} } as unknown) as AbortController);

const getBrowseApiEndpoint = () =>
  isBrowser() ? publicRuntimeConfig.endpoints.browse : publicRuntimeConfig.endpoints.browseMesh;

let controller = getAbortController();

export const requestProducts = createAsyncThunk<
  RequestProductsActionPayload,
  RequestProductsOptions,
  { rejectValue: ProductsAPIHandledRejection }
>(
  'products/requestProducts',
  async ({ query, cookie, isClientRequest }, { getState, dispatch, rejectWithValue }) => {
    const state = getState() as ReduxApplicationState;
    const {
      currencyCode: currency,
      deliveryZone,
      elevaateSessionId,
      cognitoId,
      loop54UserId,
      experiments,
    } = state.userConfiguration;

    if (canRequestSkeletonLoaders(state.products, query)) {
      dispatch(addLoadingProducts(Number(query.page) || 1));
    }

    const querystring = qs.encode({
      ...query,
      ...(currency && { currencyCode: currency }),
      ...(deliveryZone && { deliveryZone }),
      ...(loop54UserId && { userId: loop54UserId }),
      ...(query.previewing && { previewing: true }),
      ...(selectPageType(state) === PageType.Partner && {
        shortcode: query.shortcode ?? selectPartnerShortcode(state),
      }),
    });

    controller.abort();
    controller = getAbortController();

    const browseApiEndpoint = getBrowseApiEndpoint();
    const browseApiPath = selectBrowseApiPath(state);

    const apiResponse = (await fetchWithTimeout({
      ...(isClientRequest && { customAbortController: controller }),
      apiEndpoint: browseApiEndpoint,
      apiPath: (browseApiPath as unknown) as string,
      querystring,
      ...(!isClientRequest && { timeout: SERVER_SIDE_TIMEOUT }),
      useTimeout: !isClientRequest,
      headers: {
        ...(cookie && { Cookie: cookie }),
        ...(cognitoId && { 'elevaate-uid': cognitoId }),
        ...(elevaateSessionId && { 'elevaate-usid': elevaateSessionId }),
        ...(experiments && { [EXPERIMENTS_HEADER]: experiments }),
      },
    })) as Response;

    if (apiResponse?.status === 301 && apiResponse?.headers.get('location')) {
      const redirectUrl = new URL(apiResponse.headers.get('location') || '');

      return rejectWithValue({ redirectUrl: `${redirectUrl.pathname}${redirectUrl.search}` });
    }

    const {
      category,
      currencyCode,
      products: items,
      currentPage,
      totalPages,
      filters,
      totalProducts,
      productsReturnedCount,
      queryType,
      sortingOptions,
      navigation,
      partner,
    }: ProductsAPIResponse = await handleFetchResponse(apiResponse);

    return {
      category,
      ...(query.term && { searchTerm: query.term as string }),
      currencyCode,
      items: { [currentPage]: items },
      filters,
      currentPage,
      totalPages,
      totalProducts,
      productsReturnedCount,
      queryType,
      sortingOptions,
      navigation,
      partner,
    };
  },
);

export const refetchProducts = createAsyncThunk(
  'products/refetchProducts',
  async (_payload, { getState, dispatch }) => {
    const state = getState() as ReduxApplicationState;

    const queryParams = selectRouterQueryParameters(state);
    const path = selectRouterPathname(state);

    const productsQuery = {
      ...queryParams,
      path,
    };

    await dispatch(
      requestProducts({
        query: productsQuery,
        isClientRequest: true,
      }),
    );
  },
);

export const updateFilters = createAsyncThunk(
  'products/updateFilters',
  async (filterType: UpdateFiltersActionType, { getState, dispatch }) => {
    const state = getState() as ReduxApplicationState;
    const {
      router: {
        location: { pathname },
      },
    } = state;

    const queryObject = selectRequestOptionsAsQueryObject(state);
    const query = qs.stringify(queryObject);

    dispatch(
      replace(
        {
          pathname: selectRouterPathname(state),
          query,
        },
        `${pathname}?${query}`,
        { shallow: true, scroll: false },
      ),
    );

    await dispatch(
      requestProducts({
        query: {
          ...queryObject,
          path: pathname,
        },
        isClientRequest: true,
      }),
    );

    const productContainerOffsetTop = document.getElementById(PRODUCT_CONTAINER_WRAPPER)?.offsetTop;
    window.scrollTo({ top: productContainerOffsetTop, behavior: 'auto' });

    return filterType;
  },
);

export const loadPageOfProducts = createAsyncThunk(
  'products/loadPageOfProducts',
  async (pageNumber: number, { getState, dispatch }) => {
    const state = getState() as ReduxApplicationState;
    const {
      router: {
        location: { pathname },
      },
    } = state;

    const queryObject = {
      ...selectRequestOptionsAsQueryObject(state),
      page: pageNumber,
    };

    if (pageNumber > selectCurrentPage(state)) {
      const query = qs.stringify(queryObject);

      dispatch(
        replace(
          {
            pathname: selectRouterPathname(state),
            query,
          },
          `${pathname}?${query}`,
          { shallow: true, scroll: false },
        ),
      );
    }

    return await dispatch(
      requestProducts({
        query: {
          ...queryObject,
          path: pathname,
        },
        replaceProducts: false,
        isClientRequest: true,
      }),
    );
  },
);

// Register elevaate product impressions for sponsored products rendered in the page
export const elevaateProductImpressions = createAsyncThunk(
  'products/elevaateProductImpressions',
  async ({ products }: { products: Product[] }) => {
    let elevaatePageImpressionId = '';

    const sponsoredProductsQueryString = products.reduce((acc, item) => {
      if (!item.sponsoredProductData) {
        return acc;
      }

      const { sponsoredProductData } = item;
      const sponsoredProductQueryString = encodeURIComponent(
        `${sponsoredProductData['data-elevaate-aid']}|${sponsoredProductData['data-elevaate-pid']}`,
      );

      if (acc === '') {
        elevaatePageImpressionId = sponsoredProductData['data-elevaate-piid'];

        return acc.concat(sponsoredProductQueryString);
      }

      return acc.concat(`,${sponsoredProductQueryString}`);
    }, '');

    if (sponsoredProductsQueryString.length > 0) {
      const elevaateImpressionsUrlFragment = `${elevaatePageImpressionId}/renderPageCharge.json?prms=${sponsoredProductsQueryString}`;
      const elevaateImpressionsUrl = `${publicRuntimeConfig.endpoints.elevaateImpressions}/${publicRuntimeConfig.ELEVAATE_API_KEY}/${elevaateImpressionsUrlFragment}`;

      await fetch(elevaateImpressionsUrl)
        .then(handleFetchResponse)
        .then((data) => {
          console.log(`Registered impressions for: ${elevaateImpressionsUrlFragment}.`, data);
        });
    }
  },
);

export const updatePageHistory = createAsyncThunk(
  'products/updatePageHistory',
  async ({ page, productCode, productOffset }: UpdateHistoryPayload) => {
    const query = getQueryForHistoryState(page);
    history.replaceState(null, '', `${location.pathname}${query}`);
    // If not manual then page jumps up and down with a race condition because browser tries to go back in a wrong place.
    history.scrollRestoration = 'manual';
    pageHistory?.set('lastViewedProduct', { productCode, productOffset });
  },
);
